import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { CalendarIcon } from "lucide-react";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "components/ui/select";
import { Input } from "components/ui/input";

const formats = ["YYYY-MM-DD", "YYYY-M-D", "D-M-YYYY", "DD-MM-YYYY", "M-D-YYYY", "MM-DD-YYYY"];

export default function DatePickerInput({
  value,
  className,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  border,
}: {
  value: string;
  className?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  border?: string;
}) {
  const [inputValue, setInputValue] = useState("");
  const [internalDate, setInternalDate] = useState<Date | undefined>();
  const [isTyping, setIsTyping] = useState(false); // ← block sync
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [selectedMonth, setSelectedMonth] = useState(moment().month());
  const inputTimeout = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!isTyping && typeof value === "string" && value.trim()) {
      const parsed = moment(value, "YYYY-MM-DD", true);
      if (parsed.isValid()) {
        setInputValue(parsed.format("DD-MMM-YYYY"));
        setInternalDate(parsed.toDate());
        setSelectedYear(parsed.year());
        setSelectedMonth(parsed.month());
      }
    }
  }, [value, isTyping]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setIsTyping(true);
    setInputValue(val);
    if (inputTimeout.current) clearTimeout(inputTimeout.current);
    inputTimeout.current = setTimeout(() => {
      const parsed = moment(val, formats, true);
      if (parsed.isValid()) {
        const formatted = parsed.format("YYYY-MM-DD");
        setInternalDate(parsed.toDate());
        setSelectedYear(parsed.year());
        setSelectedMonth(parsed.month());
        onChange(formatted); // 🚀 pass to parent
      }
      setIsTyping(false);
    }, 250); // ← debounce: wait before re-enabling sync
  };

  const handleBlur = (event: any) => {
    onChange(event.target.value);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (!pasted) return;

    const parsed = moment(pasted, formats, true);
    if (parsed.isValid()) {
      const formatted = parsed.format("YYYY-MM-DD");
      setInputValue(parsed.format("DD-MMM-YYYY"));
      setInternalDate(parsed.toDate());
      setSelectedYear(parsed.year());
      setSelectedMonth(parsed.month());
      onChange(formatted);
    }
  };

  const handleSelectFromCalendar = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const formatted = moment(selectedDate).format("YYYY-MM-DD");
      setInternalDate(selectedDate);
      setInputValue(moment(selectedDate).format("DD-MMM-YYYY"));
      onChange(formatted); // 🚀 pass to parent
      setIsTyping(false); // allow sync again
      setIsOpenDialog(false);
    }
  };

  return (
    <div className="flex justify-center items-center m-0 p-0">
      <input
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`${className} w-[180px] text-[15px] outline-none py-4 px-5 h-[45px] border-[1px] border-r-0 rounded-tl-md rounded-bl-md bg-transparent`}
        spellCheck={false}
        disabled={disabled}
        onPaste={handlePaste}
      />

      <Popover open={isOpenDialog} onOpenChange={setIsOpenDialog}>
        <PopoverTrigger asChild>
          <button className={`bg-transparent text-black ${border} border-[1px] pe-3 h-[45px] border-l-0 rounded-tr-md rounded-br-md`}>
            <CalendarIcon className="ml-auto h-4 w-4 opacity-50 dark:text-white" />
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex flex-col gap-2 p-2">
            <div className="grid grid-cols-2 gap-2">
              <Select value={moment().month(selectedMonth).format("MMMM")} onValueChange={(month) => setSelectedMonth(moment().month(month).month())}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {moment.months().map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={String(selectedYear)} onValueChange={(year) => setSelectedYear(Number(year))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 55 }, (_, i) => moment().year() + 5 - i).map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Calendar
              mode="single"
              selected={internalDate}
              month={moment({ year: selectedYear, month: selectedMonth }).toDate()}
              onMonthChange={(newMonth) => {
                setSelectedYear(moment(newMonth).year());
                setSelectedMonth(moment(newMonth).month());
              }}
              onSelect={handleSelectFromCalendar}
              disabled={disabled}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
